import { useMemo } from "react";
import {
  AppBar,
  Box,
  BottomNavigation,
  BottomNavigationAction,
  Container,
  IconButton,
  Paper,
  Toolbar,
  Typography,
} from "@mui/material";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { authService } from "@/services/auth.service";

interface ClientLayoutProps {
  children: React.ReactNode;
  /** Titre affiché dans la TopBar du portail. */
  title?: string;
}

interface BottomTab {
  value: string;
  label: string;
  icon: JSX.Element;
  path: string;
}

const TABS: BottomTab[] = [
  { value: "home", label: "Accueil", icon: <HomeRoundedIcon />, path: "/client" },
  {
    value: "order",
    label: "Commander",
    icon: <RestaurantMenuRoundedIcon />,
    path: "/client/order/new",
  },
  {
    value: "orders",
    label: "Mes cmd",
    icon: <ReceiptLongRoundedIcon />,
    path: "/client/orders",
  },
  { value: "account", label: "Compte", icon: <PersonRoundedIcon />, path: "/client/account" },
];

/**
 * Layout principal du portail client : header mobile + bottom navigation.
 *
 * Le contenu est encapsulé dans un container max-width `sm` (compact mobile-first) ;
 * un padding bas équivalent à la hauteur de la bottom nav évite le recouvrement du contenu.
 */
export function ClientLayout({ children, title = "Défi Gourmand" }: ClientLayoutProps): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearAuth } = useAuthStore();

  const activeTab = useMemo(() => {
    const path = location.pathname;
    // « /client/orders » doit être testé avant « /client/order » (préfixe commun)
    if (path.startsWith("/client/orders")) return "orders";
    if (path.startsWith("/client/order")) return "order";
    if (path.startsWith("/client/account")) return "account";
    return "home";
  }, [location.pathname]);

  async function handleLogout(): Promise<void> {
    await authService.logout();
    clearAuth();
    navigate("/login", { replace: true });
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.default", pb: 9 }}>
      <AppBar position="sticky" color="primary" elevation={1}>
        <Toolbar sx={{ minHeight: 56 }}>
          <Typography
            variant="h6"
            sx={{ flexGrow: 1, fontWeight: 600, letterSpacing: 0.2 }}
            noWrap
          >
            {title}
          </Typography>
          <IconButton
            aria-label="Déconnexion"
            color="inherit"
            onClick={handleLogout}
            edge="end"
          >
            <LogoutRoundedIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" disableGutters sx={{ px: 2, py: 2 }}>
        {children}
      </Container>

      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (t) => t.zIndex.appBar,
        }}
      >
        <BottomNavigation
          value={activeTab}
          onChange={(_e, value: string) => {
            const tab = TABS.find((t) => t.value === value);
            if (tab) navigate(tab.path);
          }}
          showLabels
        >
          {TABS.map((tab) => (
            <BottomNavigationAction
              key={tab.value}
              label={tab.label}
              icon={tab.icon}
              value={tab.value}
            />
          ))}
        </BottomNavigation>
      </Paper>
    </Box>
  );
}
